import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TabAssignments } from './TabAssignments';
import { TabContent } from './TabContent';
import { TabEvaluations } from './TabEvaluations/TabEvaluations';
import { TabForum } from './TabForum/TabForum';
import { TabGeneral } from './TabGeneral/TabGeneral';
import { TabInteractiveClasses } from './TabInteractiveClasses/TabInteractiveClasses';
import { TabNoticeBoard } from './TabNoticeBoard/TabNoticeBoard';
import { TabStudents } from './TabStudents/TabStudents';
import { TabSupplementarMaterial } from './TabSupplementarMaterial/TabSupplementarMaterial';
import { Tabs } from 'antd';
import { isClassroomForumEnabled } from 'services/tenants';
import { useCustomSearchParams } from 'hooks/useSearchParams';
import { useShownTabs } from './tabs';
import type { IProfessorAreaClassroom } from 'interfaces/professor_area_classrooms';

export const TabList = ({ record }: { record: IProfessorAreaClassroom }) => {
  const { currentSearchParams: currentTab, setSearchParams } =
    useCustomSearchParams({
      searchParams: 'tab'
    });

  const { shownTabs } = useShownTabs();

  const [activeTabKey, setActiveTabKey] = useState<string>(
    currentTab || 'general'
  );

  const tabContent: Record<string, React.ReactNode> | null = useMemo(() => {
    if (!record) {
      return null;
    }

    return {
      general: <TabGeneral data={record} />,
      content: <TabContent />,
      forums: <TabForum classroom={record} />,
      students: <TabStudents classroom={record} />,
      interactiveClass: <TabInteractiveClasses classroom={record} />,
      supplementaryMaterials: (
        <TabSupplementarMaterial classroomId={record.id} />
      ),
      noticeBoard: <TabNoticeBoard />,
      assignments: <TabAssignments classroomRecord={record} />,
      evaluations: <TabEvaluations classroomRecord={record} />
    };
  }, [record]);

  useEffect(() => {
    if (currentTab) {
      setActiveTabKey(currentTab);
    }
  }, [currentTab]);

  // Link antigo para ?tab=forums em tenant que desligou o fórum de turma
  // deixaria o conteúdo em branco. Só checa o tenant, que é síncrono: as abas
  // de permissão ainda estão carregando neste ponto.
  useEffect(() => {
    if (activeTabKey === 'forums' && !isClassroomForumEnabled()) {
      setActiveTabKey('general');
      setSearchParams('general');
    }
  }, [activeTabKey, setSearchParams]);

  const onTabChange = useCallback(
    (tab: string) => {
      setActiveTabKey(tab);
      setSearchParams(tab);
    },
    [setSearchParams]
  );

  return (
    <Tabs
      activeKey={activeTabKey}
      onChange={onTabChange}
      className="custom-tabs"
    >
      {shownTabs.map(tab => (
        <Tabs.TabPane tab={tab.label} key={tab.key}>
          {tabContent![tab.key]}
        </Tabs.TabPane>
      ))}
    </Tabs>
  );
};
